import { Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, forkJoin } from 'rxjs';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { DrI18nService } from '@app/core/services/i18n/i18n.service';
import { ClientService } from '@app/core/services/client.service';
import { OP } from '@app/core/services/base/base-service-with-bundle.service';
import { BaseComponent } from '@app/shared/components/base/base.component';
import { ResponseColumn } from '@app/shared/models/response';
import { Message, MessageState } from '@app/shared/models/message';
import { GridOptions, GridReadyEvent, GridSizeChangedEvent } from 'ag-grid-community';
import { AggWidgetHeaderComponent } from '@app/shared/components/agg-widget-header/agg-widget-header.component';
import { AggMessageActionComponent } from '@app/shared/components/agg-renderer/agg-message-action.component';

@Component({
    selector: 'app-message-dialog',
    templateUrl: './message-dialog.component.html',
    styleUrls: ['./message-dialog.component.scss']
})
export class MessageDialogComponent extends BaseComponent implements OnInit {

    public rowData: any[];
    public gridOptions: GridOptions;
    public columnDefs: ResponseColumn[];

    constructor(
        private clientService: ClientService,
        private drI18nService: DrI18nService,
        private translateService: TranslateService,
        private dialogRef: MatDialogRef<MessageDialogComponent>) {
        super();
        this._idPrefix = 'message_dialog.';
    }

    public ngOnInit() {
        this.formatTable();

        this.clientService.getMessages().subscribe((messages) => {
            this.rowData = messages.items;
        });
    }

    public markMessageAsRead(message: Message): Observable<Message> {
        const data = [{
            op: OP.replace,
            path: 'state',
            value: MessageState.READ
        }];

        return this.clientService.patchMessage(message, data);

        // .subscribe((result) => {
        //     message.state = MessageState.READ;
        // });
    }

    public markAllAsReadAndClose() {

        const observables = [];

        this.rowData.forEach(message => {
            if (message.state === MessageState.NEW) {
                observables.push(this.markMessageAsRead(message));
            }
        });

        if (observables.length === 0) {
            this.close(true);
        }
        
        forkJoin(observables).subscribe(results => {
            this.close(true);
        });
    }

    public openLink(id: string) {
        const message = this.rowData.find(m => {
            return m.id === id;
        });

        this.markMessageAsRead(message).subscribe(result => {
            message.state = MessageState.READ;
            window.location.href = message.link;
        });
    }

    public close(cleared: boolean = false): void {
        this.dialogRef.close(cleared);
    }

    private formatTable() {
        this.columnDefs = [
            new ResponseColumn({
                headerName: this.translateService.instant('messages.dialog.table_header.date'),
                field: 'date',
                width: 200,
                minWidth: 100,
                sort: 'desc',
                cellStyle: { textAlign: 'left' },
                cellClass: 'dr-grid-class',
                headerClass: 'dr-grid-header-class',
                suppressMenu: true,
                cellRenderer: (params: any) => this.cellRenderer(params)
            }),
            new ResponseColumn({
                headerName: this.translateService.instant('messages.dialog.table_header.message'),
                field: 'message',
                width: 600,
                minWidth: 300,
                cellStyle: { textAlign: 'left' },
                cellClass: 'dr-grid-class',
                headerClass: 'dr-grid-header-class',
                suppressMenu: true,
                tooltipField: 'message',
                cellRenderer: (params: any) => this.cellRenderer(params)
            }),
            new ResponseColumn({
                headerName: this.translateService.instant('messages.dialog.table_header.action'),
                field: 'link',
                width: 200,
                minWidth: 100,
                cellClass: 'dr-grid-class',
                headerClass: 'dr-grid-header-class',
                cellRenderer: 'actionsRenderer',
                suppressMenu: true,
            })
        ];

        this.gridOptions = {
            debug: false,
            headerHeight: 40,
            domLayout: 'autoHeight',
            suppressMovableColumns: true,
            suppressNoRowsOverlay: true,
            suppressCellSelection: true,
            tooltipMouseTrack: true,
            context: { componentParent: this },
            frameworkComponents: { agColumnHeader: AggWidgetHeaderComponent, actionsRenderer: AggMessageActionComponent },
            onGridReady: (event: GridReadyEvent) => this.onGridReadyHandler(event),
            onGridSizeChanged: (event: GridSizeChangedEvent) => this.onGridSizeChangedHandler(event),
            getRowClass: (params: any) => this.getRowClass(params), 
        };
    }

    private cellRenderer(params: any): any {
        if (params.value) {
            if (params.column.colId === 'date') {
                return DrI18nService.dateTimeString(params.value);
            }
            return params.value;
        } else {
            return '-';
        }
    }

    private onGridReadyHandler(gridEvent: GridReadyEvent): void {
        gridEvent.api.sizeColumnsToFit();
    }

    private onGridSizeChangedHandler(gridEvent: GridSizeChangedEvent): void {
        gridEvent.api.sizeColumnsToFit();
    }

    private getRowClass(params: any): string | string[] {
        const cls = ['dr-row-class'];
        if (params.data.state === MessageState.NEW) {
            cls.push('highlight');
        }
        return cls;
    }

}
